import { useEffect, useState } from "react";
import axios from "axios";
import { Lock, ShieldCheck, RefreshCw, Coins } from "lucide-react";
import { API, timeAgo } from "@/lib/api";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { BonusCard } from "@/components/BonusCard";
import { PaywallDialog } from "@/components/PaywallDialog";
import { TypeBadge } from "@/components/TypeBadge";

const HERO_IMG = "https://images.unsplash.com/photo-1596518433611-6f099bf4be3c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

const Stat = ({ label, value, testid }) => (
  <div className="border-l border-white/10 pl-4">
    <div className="font-mono text-2xl sm:text-3xl font-bold text-white" data-testid={testid}>{value}</div>
    <div className="text-[11px] uppercase tracking-widest text-slate-500 mt-1">{label}</div>
  </div>
);

const LockedRow = ({ item, i }) => (
  <div className="flex items-center justify-between rounded-xl bg-[#151B28]/70 border border-white/5 px-5 py-4 select-none" style={{ animationDelay: `${i * 40}ms` }}>
    <div className="flex items-center gap-4">
      <TypeBadge type={item.bonus_type} />
      <span className="font-semibold text-slate-300 blur-[5px]">{item.casino} Casino Exclusive</span>
    </div>
    <span className="font-mono text-amber-300 blur-[6px]">$$ SC</span>
  </div>
);

export default function Landing() {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    document.title = "BonusVault — Every Casino Bonus & Free SC, Updated Daily";
    axios.get(`${API}/bonuses/teaser`).then((r) => setData(r.data));
  }, []);

  return (
    <div className="relative">
      <Header count={data?.total_count} onUnlock={() => setOpen(true)} />

      <section className="relative overflow-hidden pt-32 pb-20">
        <img src={HERO_IMG} alt="" className="absolute inset-0 h-full w-full object-cover opacity-20" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#07090E]/60 via-[#07090E]/85 to-[#07090E]" />
        <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-[1.4fr_1fr] gap-12 items-end">
          <div className="rise">
            <span className="inline-flex items-center gap-2 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-3 py-1 text-xs font-bold uppercase tracking-widest text-emerald-400">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 live-dot" /> Updated {data ? timeAgo(data.last_updated) : "today"}
            </span>
            <h1 data-testid="hero-title" className="font-display font-black text-4xl sm:text-5xl lg:text-6xl leading-[1.02] mt-6">
              Every casino bonus.<br />Every free <span className="gold-text">SC</span>.<br />One list. Three bucks.
            </h1>
            <p className="mt-6 max-w-xl text-base md:text-lg text-slate-300 leading-relaxed">
              Stop digging through forums and expired codes. We track sweepstakes & online casino welcome offers, no-deposit deals and daily free coins — re-verified every day.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <button onClick={() => setOpen(true)} data-testid="hero-checkout-button" className="rounded-full bg-emerald-500 hover:bg-emerald-400 text-[#07090E] font-bold px-8 h-14 text-base shadow-[0_0_40px_rgba(16,185,129,0.35)] transition-[background-color,transform] hover:-translate-y-0.5">
                Get Lifetime Access — $3.00
              </button>
              <span className="text-sm text-slate-400">One-time payment · No subscription</span>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-4 rise" style={{ animationDelay: "150ms" }}>
            <Stat label="Live offers" value={data?.total_count ?? "—"} testid="stat-total-count" />
            <Stat label="Est. value" value={data ? `$${Math.round(data.total_value).toLocaleString()}` : "—"} testid="stat-total-value" />
            <Stat label="Price" value="$3" testid="stat-price" />
          </div>
        </div>
      </section>

      <section className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8">
        <div className="flex items-end justify-between mb-8">
          <div>
            <p className="text-xs font-bold uppercase tracking-widest text-emerald-400/90">Free preview</p>
            <h2 className="font-display font-bold text-2xl sm:text-3xl mt-2">Today's top offers</h2>
          </div>
        </div>
        <div className="grid md:grid-cols-3 gap-5">
          {data?.visible.map((b, i) => <BonusCard key={b.id} bonus={b} compact testid={`teaser-bonus-card-visible-${i}`} />)}
        </div>

        <div className="relative mt-6">
          <div className="space-y-3 max-h-[420px] overflow-hidden">
            {data?.locked.map((item, i) => <LockedRow key={i} item={item} i={i} />)}
          </div>
          <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-b from-transparent via-[#07090E]/80 to-[#07090E]" data-testid="teaser-locked-overlay">
            <div className="rounded-2xl backdrop-blur-md bg-black/60 border border-emerald-500/25 shadow-[0_0_40px_rgba(16,185,129,0.15)] p-8 text-center max-w-sm mx-4">
              <Lock className="mx-auto text-emerald-400" />
              <p className="font-display font-bold text-xl mt-3">{data ? data.locked.length : ""}+ more offers locked</p>
              <p className="text-sm text-slate-400 mt-2">Unlock the full list with codes, claim links & state eligibility.</p>
              <button onClick={() => setOpen(true)} data-testid="teaser-unlock-button" className="mt-5 w-full rounded-full bg-emerald-500 hover:bg-emerald-400 text-[#07090E] font-bold h-12 transition-colors">Unlock for $3</button>
            </div>
          </div>
        </div>
      </section>

      <section className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 mt-24 grid md:grid-cols-3 gap-10">
        {[
          [RefreshCw, "Refreshed daily", "New offers added and dead ones removed every day, so you never chase an expired code."],
          [Coins, "Free SC first", "Sweepstakes coins you can redeem — no-deposit and daily login freebies front and center."],
          [ShieldCheck, "Pay once, keep it", "$3 one time via Stripe. Restore access anytime with your email."],
        ].map(([Icon, t, d]) => (
          <div key={t}>
            <Icon className="text-amber-400" />
            <h3 className="font-bold text-lg mt-4">{t}</h3>
            <p className="text-sm text-slate-400 mt-2 leading-relaxed">{d}</p>
          </div>
        ))}
      </section>

      <Footer />

      <div className="md:hidden fixed bottom-0 inset-x-0 z-40 p-3 backdrop-blur-xl bg-[#07090E]/85 border-t border-white/10">
        <button onClick={() => setOpen(true)} data-testid="mobile-sticky-cta" className="w-full h-12 rounded-full bg-emerald-500 text-[#07090E] font-bold">Unlock all offers — $3</button>
      </div>

      <PaywallDialog open={open} onOpenChange={setOpen} />
    </div>
  );
}
